import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getUnixTime } from "date-fns"
import { useEffect, useRef, useState } from "react"
import { Link, Redirect, useHistory } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "../../hooks/useRedux"
import { RouteComponentProps } from "react-router-dom"
import Heading from "../utils/Heading"
import MealSelection from "../utils/MealSelection"
import { cleanBasketBookings, removeBasket } from '../../store/basketSlice'
import api from '../../api';
import axios, { AxiosError } from "axios"
import { ORDER_STATUS_CONFIRMED } from '../../store/orderSlice';
import { fetchBookings } from '../../store/bookingSlice';

export default function CheckoutForm(props: RouteComponentProps<{ id?: string }>) {
  const history = useHistory()
  const id = props.match.params.id
  const stripe = useStripe();
  const elements = useElements();

  const terms = useAppSelector(state => state.term.terms.filter((term) => {
    return term.end >= getUnixTime(new Date())
  }).sort((a, b) => {
    return (a.start > b.start) ? -1 : 1
  }))
  const dispatch = useAppDispatch()

  const meals = useAppSelector(state => state.meals.meals)
  const menus = useAppSelector(state => state.menus.menus)
  const baskets = useAppSelector(state => state.basket.baskets)
  const currentStudent = useAppSelector(state => state.students.students.find((student => student.id === id)))
  const [paymentRequired, setPaymentRequired] = useState<boolean>(false)
  const [processing, setProcessing] = useState<boolean>(false)
  const [subItems, setSubItems] = useState<{
    [key: string]: {
      quantity?: number
      cost: number
    }
  }>({});
  // const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
  const [error, setError] = useState<string>('');
  const [orderId, setOrderId] = useState<string | null>(null);
  const [disabled, setDisabled] = useState(true);
  const [clientSecret, setClientSecret] = useState('');
  // Synchronous re-entrancy lock: `processing` state updates async, so a fast
  // double-submit can pass the guard twice before React re-renders.
  const submittingRef = useRef(false);

  useEffect(() => {
    dispatch(cleanBasketBookings({ studentId: id }));
  }, [id, dispatch]);
  // const [paymentRequired, setPaymentRequired] = useState<>(false)
  if (!id || !currentStudent) {
    return <Redirect push to="/" />
  }
  
  const proceedToThankYou = () => {
    dispatch(fetchBookings({ force: true }))
    history.push("/thanks")
    dispatch(removeBasket(id));
  }
  
  const handleChange = async (event: any) => {
    // Listen for changes in the CardElement
    // and display any errors as the customer types their card details
    setDisabled(event.empty);
    setError(event.error ? event.error.message : "");
  };
  const handleConfirm = async (event: any) => {
    if (processing) return
    const basket = baskets[id]
    setProcessing(true)
    api
      .post('/order', {
        ...basket,
      })
      .then(response => {
        if (response.data.data.status === ORDER_STATUS_CONFIRMED) {
          proceedToThankYou();
        } else {
          setPaymentRequired(true);
          setClientSecret(response.data.client_secret);
          let rows = {};
          if (response.data.cost !== response.data.total) {
             rows = {
              "Outstanding Balance": {
                cost: Math.abs(response.data.balance)
              },
            };
          }
          rows = {
            ...rows,
            "Meals": {
              quantity: Object.keys(baskets[currentStudent.id].bookings).length,
              cost: response.data.total
            },
            "Total": {
              cost: response.data.cost
            }
          }
          setSubItems(rows)
          setOrderId(response.data.data.id);
          setError('');
        }
      })
      .catch((error: Error | AxiosError) => {
        if (axios.isAxiosError(error)) {
          if (error.response?.status === 400) {
            return
          }
        }
        setError("We've been unable to prepare your order at this time, please try again later.")
      })
      .finally(() => {
        setProcessing(false)
      })
  }
  const completeOrder = () => {
    return api
      .post(`/complete/${orderId}`, {
        'version':1
      })
      .then(response => {
        if (response.data.data.status === ORDER_STATUS_CONFIRMED) {
          setError('');
          setClientSecret('')
          proceedToThankYou();
        }

      })
      .catch((error: Error | AxiosError) => {
        setError("We've been unable to prepare your order at this time, please try again later.")
      })
      .finally(() => {
        setProcessing(false)
        submittingRef.current = false
      })
  }
  const handleSubmit = async (event: any) => {
    // Block native form submission.
    event.preventDefault();

    if (!stripe || !elements || processing || submittingRef.current) {
      // Stripe.js has not loaded yet, or a submit is already in flight.
      return;
    }

    submittingRef.current = true;
    setProcessing(true);
    // Get a reference to a mounted CardElement. Elements knows how
    // to find your CardElement because there can only ever be one of
    // each type of element.
    const cardElement = elements.getElement(CardElement);
    if (cardElement) {
      const payload = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement
        }
      });
      if (payload.error) {
        if (payload.error.code === 'payment_intent_unexpected_state') {
          // The PaymentIntent is already in a terminal state — verify its ACTUAL
          // status before completing. Only 'succeeded' means the money was taken;
          // 'canceled'/other must still surface as a failure.
          try {
            const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);
            if (paymentIntent?.status === 'succeeded') {
              await completeOrder();
              return;
            }
          } catch {
            // retrievePaymentIntent failed (network, SDK error) — reset locks and
            // surface a generic error so the user can retry.
            setError("We've been unable to prepare your order at this time, please try again later.");
            setProcessing(false);
            submittingRef.current = false;
            return;
          }
        }
        setError(`Payment failed ${payload.error.message}`);
        setProcessing(false);
        submittingRef.current = false;
      } else {
        await completeOrder();
      }
    } else {
      setProcessing(false);
      submittingRef.current = false;
    }
  };
  let itemRows: any[] = [];

  Object.entries(subItems).forEach(
    ([key, value]) => {
      itemRows.push(
        <div className="flex flex-row justify-between p-2">
          <div className="text-black text-left">
            {value.quantity ?? ''} {key.charAt(0).toUpperCase() + key.slice(1)}:
          </div>
          <div className="text-black text-right">
            {new Intl.NumberFormat('en-GB', {
              style: 'currency',
              currency: 'GBP'
            }).format(value.cost / 100)}
          </div>
        </div>

      )
    }
  );
  return (
    <div className="py-8">
      {!paymentRequired &&
        <div className="panel my-4">
          <div className="mb-4">
            <Heading
              text="Confirm your order" />
          </div>

          {error?.length > 0 &&
            <div className="error text-red-500">{error}</div>
          }

          <MealSelection
            currentStudent={currentStudent}
            terms={terms}
            basket={baskets[currentStudent.id] ?? {}}
            meals={meals}
            menus={menus}
            readonly={true}
          />
          <div className="buttonHolder my-6 flex flex-row justify-between">
            <Link
              className="brand-button-tertiary decoration-none"
              to={{
                pathname: `/`,
                state: { title: 'Go Back' },
              }}
            >Go Back</Link>
            <button
              className={`brand-button text-white`}
              type="submit"
            onClick={handleConfirm}
              disabled={processing}
          >
            {processing && 
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block align-middle" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            }
              Confirm
            </button>
          </div>
        </div>
      }
      {paymentRequired &&
        <div className="panel my-4">
          <div className="mb-3">
            <Heading
              text="Enter your payment details" />
          </div>
          <form onSubmit={handleSubmit}>
            {error?.length > 0 &&
              <div className="error text-red-500">{error}</div>
            }
            <div className="bg-white border appearance-none border-gray-400 my-4 p-6 rounded-2xl">
              <CardElement onChange={handleChange} />
            </div>
            {itemRows}
            <div className="buttonHolder my-6 flex flex-row justify-between">
              <Link
                className="brand-button-tertiary decoration-none"
                to={{
                  pathname: `/`,
                  state: { title: 'Go Back' },
                }}
              >Go Back</Link>
            <button className="brand-button text-white" type="submit" disabled={!stripe || disabled || processing}>
              {processing &&
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block align-middle" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              }
                Pay Now
              </button>
            </div>
          </form>
        </div>
      }
    </div>
  );
};